import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';

import { BizyMemory } from '../schemas/bizy-memories';
import { listOf } from '../schemas/utils';
import { headers } from './meta';

// ─── Query Keys ──────────────────────────────────────────────────────────────

enum QueryKey {
	Memories = 'bizy-user-memories',
}

export const bizyMemoriesQueryKey = () => [QueryKey.Memories];

// ─── Path helpers ────────────────────────────────────────────────────────────

export const bizyMemoriesPath = (prefix = '') => `${prefix}/bizy/memories`;
export const bizyMemoryPath = (id: string, prefix = '') => `${prefix}/bizy/memories/${id}`;

// ─── API response types ──────────────────────────────────────────────────────

interface BizyMemoriesIndexAPIResponse {
	memories: Array<Record<string, unknown>>;
}

interface BizyMemoryAPIResponse {
	memory: Record<string, unknown>;
}

// ─── Query hooks ─────────────────────────────────────────────────────────────

/**
 * Fetches all of the current user's Bizy memories.
 *
 * GET /api/v2/bizy/memories
 */
export const useBizyMemories = () => {
	const http = useHttpClient();

	return useQuery({
		queryKey: bizyMemoriesQueryKey(),
		queryFn: async () => {
			const res = await http.get<BizyMemoriesIndexAPIResponse>(bizyMemoriesPath(), {
				headers,
			});

			return listOf(BizyMemory).parse(res.data.memories);
		},
		refetchOnMount: 'always',
	});
};

// ─── Mutation hooks ──────────────────────────────────────────────────────────

/**
 * Updates a memory's text and/or category.
 *
 * PATCH /api/v2/bizy/memories/:id
 */
export const useBizyUpdateMemoryMutation = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, text, category }: { id: string; text?: string; category?: string }): Promise<BizyMemory> => {
			const res = await http.patch<BizyMemoryAPIResponse>(bizyMemoryPath(id), { text, category }, { headers });
			return BizyMemory.parse(res.data.memory);
		},
		onSuccess: () => {
			queryClient.invalidateQueries(bizyMemoriesQueryKey());
		},
	});
};

/**
 * Deletes a Bizy memory.
 *
 * DELETE /api/v2/bizy/memories/:id
 */
export const useBizyDeleteMemoryMutation = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string): Promise<void> => {
			await http.delete(bizyMemoryPath(id));
		},
		onSuccess: () => {
			queryClient.invalidateQueries(bizyMemoriesQueryKey());
		},
	});
};
